import { Link } from "react-router-dom";
import { Crown, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

const DISMISS_KEY = "lumioos_trial_banner_dismissed_at";

// Sticky top banner shown inside the authenticated app when the user is on
// an active 7-day Pro free trial. Tells them how many days remain and links
// to /app/pricing so they can convert before the trial ends. Dismissible —
// hidden state is remembered per-session-day so we don't pester users on
// every page navigation, but the banner reappears the next calendar day.
export default function TrialBanner() {
  const { user } = useAuth();
  const { t } = useT();
  // Compute initial dismissed state synchronously from localStorage so we
  // never flash the banner before useEffect hides it. Stored value is the
  // ISO date — banner reappears the next calendar day to keep urgency fresh.
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    const last = localStorage.getItem(DISMISS_KEY);
    if (!last) return false;
    return last === new Date().toISOString().slice(0, 10);
  });

  if (!user?.is_trial || dismissed) return null;
  const days = user.trial_days_remaining ?? 0;
  // Switch to a louder "urgent" red when 2 days or less remain so users feel the
  // deadline and convert before being downgraded.
  const urgent = days <= 2;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10));
    setDismissed(true);
  };

  return (
    <div
      data-testid="trial-banner"
      className={`relative w-full text-white text-sm font-semibold ${
        urgent
          ? "bg-gradient-to-r from-red-600 via-rose-600 to-orange-600"
          : "bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-3 text-center flex-wrap">
        {urgent ? (
          <Crown className="w-4 h-4 shrink-0" />
        ) : (
          <Sparkles className="w-4 h-4 shrink-0" />
        )}
        <span data-testid="trial-banner-text">
          {days <= 0
            ? t("trial_banner_ending_today")
            : t("trial_banner_text", { days })}
        </span>
        <Link
          to="/app/pricing"
          data-testid="trial-banner-cta"
          className="inline-flex items-center gap-1 bg-white text-zinc-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 transition-colors"
        >
          {t("trial_banner_cta")}
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        data-testid="trial-banner-close"
        aria-label={t("trial_banner_close")}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/20 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
